// src\themes\default\components\ThirdGridLine.tsx
'use client';

import { Heading } from '@/components/ui/Heading';
import Image from 'next/image';
import Link from 'next/link';

interface ImageInfo {
    url: string;
}

interface GridItem {
    title?: string | null;
    buttonContent?: string | null;
    link?: string | null;
    image: ImageInfo;
}

interface ThirdGridLineProps {
    data?: {
        title?: string | null;
        link?: string | null;
        buttonContent?: string | null;
        items?: GridItem[];
    };
}

export default function ThirdGridLine({ data }: ThirdGridLineProps) {
    if (!data || !data.items || data.items.length < 3) {
        return null;
    }

    const items = data.items.slice(0, 3);

    return (
        <div className="col-span-12 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
                {items.map((item, index) => {
                    const content = (
                        <div className="relative w-full aspect-[4/5] overflow-hidden group">
                            {item.image?.url && (
                                <Image
                                    src={item.image.url}
                                    alt={item.title || 'image'}
                                    fill
                                    className="object-cover transition-transform duration-300 group-hover:scale-105"
                                />
                            )}
                            {(item.title || item.buttonContent) && (
                                <div className="absolute bottom-0 inset-x-0 space-y-1 p-3 flex flex-col items-start text-primary-0 z-10 bg-gradient-to-t from-black/50 to-transparent">
                                    {item.title && (
                                        <span className="text-primary-0 text-sm font-medium">
                                            {item.title}
                                        </span>
                                    )}
                                    {item.buttonContent && (
                                        <span className="inline-block border-b border-primary-0 text-xs">
                                            {item.buttonContent}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    );

                    return (
                        <div key={index}>
                            {item.link ? <Link href={item.link}>{content}</Link> : content}
                        </div>
                    );
                })}
            </div>

            {(data.title || data.buttonContent) && (
                <div className="flex items-center justify-between">
                    {data.title && <Heading level={2}>{data.title}</Heading>}
                    {data.buttonContent && data.link && (
                        <Link
                            href={data.link}
                            className="flex items-center gap-2 border rounded px-4 py-2 text-sm"
                        >
                            {data.buttonContent}
                        </Link>
                    )}
                </div>
            )}
        </div>
    );
}